<template>
  <div>
    <PageAllow controller="ProductionPlans" action="index" />
    <Breadcrumbs :items="items" />

    <v-sheet class="transparent-background-color">
      <v-row dense class="mx-5">
        <v-col cols="12" sm="12" md="4">
          <v-row dense align="center" class="pa-1">
            <SearcherDatetimeRange
              type="datetime"
              v-model="datetimeRage"
              :label="$t('ProductionPlans.Fields.created_time')"
              removeIcon
            />
          </v-row>
        </v-col>
        <v-col cols="12" sm="12" md="8" align-self="center">
          <v-row dense align="center" class="search-controls">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="px-0 py-0 caption font-weight-bold grey--text"
                  color="primary"
                  @click.stop="onSearch()"
                >
                  <v-icon small class="pe-1">fa-search</v-icon>
                  {{ $t("Search.search") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.searchTip") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="caption font-weight-bold grey--text"
                  @click.stop="resetSearch()"
                >
                  <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                  {{ $t("Search.reset") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.resetTip") }}</span>
            </v-tooltip>
            <v-divider vertical />
            <v-btn
              class="caption font-weight-bold grey--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
              small
              text
              @click.stop="$store.commit('toggleSearchPanel')"
            >
              {{ $t("Search.pro") }}
              <v-icon small class="ps-1">{{
                $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
              }}</v-icon>
            </v-btn>
            <v-spacer />
            <v-btn
              v-if="$permission.permissionAllow('ProductionPlans', 'add')"
              small
              color="primary"
              href="/#/production-plans/new"
              class="ms-1"
            >
              <v-icon x-small class="pe-1">fa-plus</v-icon>
              {{ $t("CRUD.newInfo", [$t("ProductionPlans.ObjectName")]) }}
            </v-btn>
            <!-- 导出生产计划 -->
            <v-btn
              small
              color="print"
              dark
              class="ms-1"
              @click.stop="exports()"
            >
              <v-icon x-small class="pe-1">fa-file-export</v-icon>
              {{ $t("Export.export", [$t("ProductionPlans.ObjectName")]) }}
            </v-btn>
            <!-- 批量详情导出 -->
            <v-btn
              v-if="$permission.permissionAllow('ProductionPlans','batchDetailExport')"
              small
              color="print"
              dark
              class="ms-1"
              @click.stop="batchDetailExport()"
            >
              <v-icon x-small class="pe-1">fa-file-export</v-icon>
              {{ $t("ProductionPlans.batchExport") }}
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
      <v-expansion-panels
        v-model="$store.getters.panels"
        multiple
        flat
        accordion
      >
        <v-expansion-panel class="transparent-background-color">
          <v-expansion-panel-content>
            <v-row dense align="center">
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="production_code"
                  v-model="options.production_code"
                  @text="productionPlansKeyWord = $event"
                  :label="$t('ProductionPlans.productionPlansKeyWord')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherStock v-model="stocksSelected" :stock-types="[0]" />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherMaterials v-model="materialSelected" />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherStatus
                  v-model="options.status"
                  :items="$consts.pruductionPlan.status"
                  :label="$t('ProductionPlans.productionPlansStatus.title')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherStatus
                  v-model="options.production_types"
                  :items="$consts.pruductionPlan.type"
                  :label="$t('ProductionPlans.productionType.title')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherCancelFlgs v-model="options.cancel_flgs" />
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div class="mx-4 mb-4">
        <v-data-table
          :headers="headers"
          :items="production_plans"
          :options.sync="options"
          :page.sync="paging.page"
          :server-items-length="paging.count"
          :loading="loading"
          multi-sort
          class="elevation-2 px-4 pt-1 sticky-table"
          id="sticky-table"
          :class="stickTableCols ? 'sticky-first-col sticky-last-col' : ''"
          fixed-header
          :footer-props="$t('$config.footer-props')"
        >
          <template v-slot:header.actions="{ header }">
            {{ header.text }}
            <v-icon x-small>fa-sliders-h</v-icon>
          </template>
          <template v-slot:item.production_code="{ item, index }">
            <span
              class="text-no-wrap caption font-weight-bold"
              :v-resize="!index ? $stickTableResizer() : null"
            >
              {{ item.production_code }}
            </span>
            <br />
            <span
              class="text-no-wrap caption secondary--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
            >
              {{ item.created | formatTime }}
            </span>
          </template>
          <template v-slot:item.production_type="{ item }">
            <v-chip
              small
              label
              :color="item.production_type | productionPlansTypeColor"
              :text-color="item.production_type | productionPlansTypeTextColor"
            >
              {{ item.production_type | productionPlansTypeText }}
            </v-chip>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip
              small
              label
              :color="item.status | productionPlansStatusColor"
              :text-color="item.status | productionPlansStatusTextColor"
            >
              {{ item.status | productionPlansStatusText }}
            </v-chip>
            <br />
            <v-chip v-if="item.cancel_flg" x-small label outlined color="danger"
              >{{ $t("CRUD.cancelled") }}
            </v-chip>
          </template>
          <!-- 完成时间 -->
          <template v-slot:item.complete_date="{ item }">
            {{ item.complete_date | formatTime }}
          </template>
          <template v-slot:item.actions="{ item }">
            <ProductionPlanActions
              :production_plan="item"
              showView
              :showEdit="item.actions.account_edit"
              :showApprove="item.actions.approve"
              :showCancel="item.actions.cancel"
              :showConfirm="item.actions.confirm"
              :showStockIN="item.actions.in"
              :showStockOUT="item.actions.out"
              @done="getProductionPlansFromAPI()"
              @shelveOnBatch="
                stock_in_shelve_on = $event;
                positionSelectedAlert = true;
              "
            />
          </template>
        </v-data-table>
      </div>
    </v-sheet>
  </div>
</template>
<script>
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import { mapState } from "vuex";
import ProductionPlans from "@/api/production_plans";
import SearcherDatetimeRange from "@/components/plugins/SearcherDatetimeRange";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
import SearcherStock from "@/components/plugins/SearcherStock";
import SearcherMaterials from "@/components/plugins/SearcherMaterials";
import SearcherStatus from "@/components/plugins/SearcherStatus";
import SearcherCancelFlgs from "@/components/plugins/SearcherCancelFlgs";
import ProductionPlanActions from "./plugins/Actions";

const defaultOptions = {
  created_time_from:
    new Date(new Date().getTime() - 30 * 24 * 3600 * 1000)
      .toISOString()
      .substr(0, 10) + " 00:00:00",
  created_time_to: new Date().toISOString().substr(0, 10) + " 23:59:59",
  production_code: "",
  status: [1, 2, 3, 4, 5],
  cancel_flgs: [0],
  production_types: [1, 2],
  stock_id: null,
  material_ids: [],
};

export default {
  components: {
    PageAllow,
    Breadcrumbs,
    SearcherDatetimeRange,
    SearcherKeyword,
    SearcherStock,
    SearcherMaterials,
    SearcherStatus,
    SearcherCancelFlgs,
    ProductionPlanActions,
  },

  computed: {
    items: () => [
      {
        text: i18n.t("Nav.home"),
        disabled: false,
        href: "/home",
      },
      {
        text: i18n.t("Nav.productionManagement"),
        disabled: false,
        replace: true,
        href: "/production-plans",
      },
      {
        text: i18n.t("Nav.productionPlans"),
        disabled: true,
        href: "/production-plans",
      },
    ],

    headers() {
      return [
        {
          text:
            i18n.t("ProductionPlans.Fields.order_code") +
            " / " +
            i18n.t("ProductionPlans.Fields.created_time"),
          align: "start",
          sortable: false,
          value: "production_code",
        },
        {
          text: i18n.t("ProductionPlans.Fields.stock_name"),
          align: "start",
          sortable: false,
          value: "stock.name",
        },
        {
          text: i18n.t("ProductionPlans.Fields.type"),
          align: "start",
          sortable: false,
          value: "production_type",
        },
        {
          text: i18n.t("ProductionPlans.Fields.status"),
          align: "center",
          sortable: false,
          value: "status",
        },
        {
          text: i18n.t("ProductionPlans.Fields.complete_date"),
          align: "center",
          sortable: false,
          value: "complete_date"
        },
        {
          text: i18n.t("TableHeaders.actions"),
          align: "start",
          sortable: false,
          value: "actions",
        },
      ];
    },

    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading", "settings", "stickTableCols"]),
  },

  data: () => ({
    production_plans: [],
    paging: {},
    options: JSON.parse(
      sessionStorage.getItem("production-plans-options")
        ? sessionStorage.getItem("production-plans-options")
        : JSON.stringify(defaultOptions)
    ),
    productionPlansKeyWord: "",
    stocksSelected: null,
    materialSelected: [],
    datetimeRage: [
      new Date(new Date().getTime() - 30 * 24 * 3600 * 1000)
        .toISOString()
        .substr(0, 10) + " 00:00:00",
      new Date().toISOString().substr(0, 10) + " 23:59:59",
    ],
    positionSelectedAlert: false,
    position: {},
    stock_in_shelve_on: {},
  }),

  mounted() {
    this.$commonUtils.loadOptions("production-plans", ["stockSelected", "materialSelected"]).then(
      function (data) {
        if (data.options) {
          this.options = data.options;
          if (
            typeof this.options.created_time_from != "undefined" &&
            typeof this.options.created_time_to != "undefined"
          )
            this.datetimeRage = [
              this.options.created_time_from,
              this.options.created_time_to,
            ];
        } else this.resetSearch();

        data.selecteds.forEach((selected) => {
          switch (selected.key) {
            case "stockSelected":
              this.stocksSelected = selected.value;
              break;
            case "materialSelected":
              this.materialSelected = selected.value;
              break;
          }
        });
      }.bind(this)
    );
  },

  methods: {
    resetSearch() {
      this.stocksSelected = null;
      this.materialSelected = [];
      this.datetimeRage = [
        new Date(new Date().getTime() - 30 * 24 * 3600 * 1000)
          .toISOString()
          .substr(0, 10) + " 00:00:00",
        new Date().toISOString().substr(0, 10) + " 23:59:59",
      ];
      this.options = JSON.parse(JSON.stringify(defaultOptions));
      [this.$refs.production_code].forEach((k) => {
        if (k) k.clear();
      });
    },
    onSearch() {
      this.options = Object.assign({}, this.options, {
        production_code: this.productionPlansKeyWord,
      });
    },
    getProductionPlansFromAPI() {
      var options = JSON.parse(JSON.stringify(this.options));
      ProductionPlans.fetch(options).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.production_plans = response.data.production_plans;
            this.paging = response.data.paging;
          } else if (response.data.error.code.isNoResultSet()) {
            this.options.page = 1;
          }
        }.bind(this)
      );
    },
    exports() {
      let options = JSON.parse(JSON.stringify(this.options));
      ProductionPlans.exports(options);
    },
    batchDetailExport() {
      let options = JSON.parse(JSON.stringify(this.options));
      ProductionPlans.batchDetailExport(options);
    }
  },

  watch: {
    options: {
      deep: true,
      handler() {
        this.getProductionPlansFromAPI();
        sessionStorage.setItem(
          "production-plans-options",
          JSON.stringify(this.options)
        );
      },
    },
    stocksSelected: {
      handler() {
        this.options.stock_id = this.stocksSelected
          ? this.stocksSelected.id
          : 0;
        sessionStorage.setItem(
          "production-plans-stockSelected",
          JSON.stringify(this.stocksSelected)
        );
      },
    },
    materialSelected: {
      handler() {
        this.options.material_ids = this.materialSelected
          ? this.materialSelected.map((e) => {
              return e.id;
            })
          : "";
        sessionStorage.setItem(
          "production-plans-materialSelected",
          JSON.stringify(this.materialSelected)
        );
      },
    },
    datetimeRage: {
      handler() {
        if (this.datetimeRage[0] == null || this.datetimeRage[0] == "") {
          this.options.created_time_from =
            new Date(new Date().getTime() - 30 * 24 * 3600 * 1000)
              .toISOString()
              .substr(0, 10) + " 00:00:00";
        } else {
          this.options.created_time_from = this.datetimeRage[0];
        }

        if (this.datetimeRage[1] == null || this.datetimeRage[1] == "") {
          this.options.created_time_to =
            new Date().toISOString().substr(0, 10) + " 23:59:59";
        } else {
          this.options.created_time_to = this.datetimeRage[1];
        }
      },
    },
  },
};
</script>